import { memo } from 'react';
import { WifiOff } from 'lucide-react';
import { cn } from '@cyphex/ui';
import type { ConnectionQuality } from '../media/contract';

export interface QualityIndicatorProps {
  quality: ConnectionQuality;
  /** Person the reading belongs to — read out by screen readers. */
  name?: string;
  size?: 'sm' | 'md';
  className?: string;
}

interface QualityMeta {
  readonly bars: number;
  readonly tone: string;
  readonly label: string;
}

const META: Readonly<Record<ConnectionQuality, QualityMeta>> = {
  excellent: { bars: 3, tone: 'bg-success', label: 'Strong connection' },
  good: { bars: 2, tone: 'bg-success', label: 'Good connection' },
  poor: { bars: 1, tone: 'bg-warning', label: 'Weak connection' },
  lost: { bars: 0, tone: 'bg-destructive', label: 'Connection lost' },
};

const HEIGHTS: readonly string[] = ['h-1', 'h-2', 'h-3'];

/**
 * Three-bar signal reading, per participant. It is deliberately not a number:
 * "2.4 % loss" tells a user nothing, "weak connection" tells them to stop
 * sharing their screen.
 */
export const QualityIndicator = memo(function QualityIndicator({
  quality, name, size = 'sm', className,
}: QualityIndicatorProps) {
  const meta = META[quality];
  const description = name ? `${name}: ${meta.label}` : meta.label;

  if (quality === 'lost') {
    return (
      <WifiOff
        size={size === 'sm' ? 14 : 16}
        strokeWidth={2.5}
        className={cn('text-destructive', className)}
        aria-label={description}
      />
    );
  }

  return (
    <span
      role="img"
      aria-label={description}
      title={description}
      className={cn('inline-flex items-end gap-px', size === 'md' && 'gap-0.5', className)}
    >
      {HEIGHTS.map((height, index) => (
        <span
          key={height}
          aria-hidden
          className={cn(
            'w-0.5 rounded-sm',
            size === 'md' && 'w-1',
            height,
            index < meta.bars ? meta.tone : 'bg-muted-foreground/30',
          )}
        />
      ))}
    </span>
  );
});
